import React, { ReactElement, useEffect } from 'react';
import { Button, ButtonGroup, Dropdown, Tooltip, useStyles2 } from '@grafana/ui';
import { css } from '@emotion/css';
import { ChatContextItem } from '../../context/types';
import { useAssistant } from '../../hook';
import { GrafanaTheme2 } from '@grafana/data';
import { OpenAssistantProps } from '../../sidebar';
import { reportInteraction } from '@grafana/runtime';

export interface OpenAssistantSplitButtonProps {
  /** Prompt to pass to the openAssistant function. */
  prompt: string;
  /** Origin of the request that opened the assistant. */
  origin: string;
  /** Context to pass to the openAssistant function. Optional, defaults to undefined. */
  context?: ChatContextItem[];
  /** Whether to automatically send the prompt. Optional, defaults to true. */
  autoSend?: boolean;
  /** Text to display on the button. Optional, defaults to 'Chat with Assistant' */
  title?: string;
  /** Button size, defaults to sm */
  size?: 'xs' | 'sm' | 'md' | 'lg';
  /** Function to call when the main button is clicked. Optional. */
  onClick?: () => void;
  /** Additional menu items to show in the dropdown. Optional. */
  menuItems?: ReactElement;
  /** Tooltip text to display on hover. Optional. */
  tooltip?: string;
}

/**
 * A split button component that opens the Grafana Assistant with a dropdown for additional actions.
 * The gradient border wraps around both buttons as a unified control.
 */
export function OpenAssistantSplitButton(props: OpenAssistantSplitButtonProps) {
  const { isAvailable, openAssistant } = useAssistant();
  if (!isAvailable || !openAssistant) {
    return null;
  }
  return <OpenAssistantSplitButtonView {...props} openAssistant={openAssistant} />;
}

export function OpenAssistantSplitButtonView({
  prompt,
  origin,
  context,
  autoSend = true,
  title = 'Chat with Assistant',
  size = 'sm',
  onClick,
  menuItems,
  tooltip,
  openAssistant,
}: OpenAssistantSplitButtonProps & {
  openAssistant: (props: OpenAssistantProps) => void;
}) {
  const styles = useStyles2(getStyles);

  useEffect(() => {
    reportInteraction('grafana_assistant_app_open_sidebar_split_button_displayed', {
      from: origin,
    });
  }, [origin]);

  const handleMainClick = () => {
    onClick?.();
    openAssistant({ prompt, context, autoSend, origin });
  };

  const wrapperContent = (
    <div className={styles.wrapper}>
      <ButtonGroup className={styles.buttonGroup}>
        <Button
          icon="ai-sparkle"
          onClick={handleMainClick}
          variant="secondary"
          fill="solid"
          size={size}
          aria-label={title}
          data-testid="assistant-split-button-main"
        >
          {title}
        </Button>
        {menuItems && (
          <Dropdown overlay={menuItems}>
            <Button
              variant="secondary"
              icon="angle-down"
              size={size}
              aria-label="More AI actions"
              className={styles.dropdownButton}
              data-testid="assistant-split-button-dropdown"
            />
          </Dropdown>
        )}
      </ButtonGroup>
    </div>
  );

  return tooltip ? <Tooltip content={tooltip}>{wrapperContent}</Tooltip> : wrapperContent;
}

const getStyles = (theme: GrafanaTheme2) => {
  const baseBackground = theme.colors.secondary.main;
  const elevatedBackground = theme.colors.emphasize(baseBackground, 0.05);
  const underlyingColor = theme.colors.background.canvas;
  const borderWidth = 1;
  const outerRadius = theme.shape.radius.default;
  const innerRadius = `max(calc(${outerRadius} - ${borderWidth}px), 1px)`;

  return {
    wrapper: css({
      label: 'assistant-split-button-wrapper',
      position: 'relative',
      isolation: 'isolate',
      display: 'inline-block',
      // Gradient border around the entire button group
      '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        borderRadius: outerRadius,
        background: 'linear-gradient(90deg, rgb(168, 85, 247), rgb(249, 115, 22))',
        zIndex: -1,
        pointerEvents: 'none',
      },
      // Solid background layer
      '&::after': {
        content: '""',
        position: 'absolute',
        inset: `${borderWidth}px`,
        borderRadius: innerRadius,
        background: `linear-gradient(${baseBackground}, ${baseBackground}), ${underlyingColor}`,
        zIndex: -1,
        pointerEvents: 'none',
      },
      '&:hover::after': {
        background: `linear-gradient(${elevatedBackground}, ${elevatedBackground}), ${underlyingColor}`,
      },
    }),
    buttonGroup: css({
      label: 'assistant-split-button-group',
      gap: 0,
      position: 'relative',
      zIndex: 1,
      // Make buttons transparent to show the wrapper's gradient
      '& button': {
        border: 'none',
        background: 'transparent !important',
        '&:hover': {
          background: 'transparent !important',
        },
      },
    }),
    dropdownButton: css({
      padding: '0 6px',
    }),
  };
};
